import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './CookiePolicy.css';

const CookiePolicy = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('cookie');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="cookie-policy-container">
      <div className="cookie-policy-header">
        <div className="cookie-policy-header-content">
          <h1>{t('header.title')}</h1>
          <p className="cookie-policy-subtitle">
            {t('header.subtitle')}
          </p>
          <p className="cookie-policy-date">{t('header.lastUpdated')}</p>
        </div>
      </div>

      <div className="cookie-policy-content">
        <nav className="cookie-policy-nav">
          <h3>{t('navigation.title')}</h3>
          <ul>
            <li><a href="#what-are-cookies">{t('navigation.whatAreCookies')}</a></li>
            <li><a href="#why-we-use-cookies">{t('navigation.whyWeUseCookies')}</a></li>
            <li><a href="#types-of-cookies">{t('navigation.typesOfCookies')}</a></li>
            <li><a href="#first-party-cookies">{t('navigation.firstPartyCookies')}</a></li>
            <li><a href="#third-party-cookies">{t('navigation.thirdPartyCookies')}</a></li>
            <li><a href="#managing-cookies">{t('navigation.managingCookies')}</a></li>
            <li><a href="#cookie-retention">{t('navigation.cookieRetention')}</a></li>
            <li><a href="#consent">{t('navigation.consent')}</a></li>
            <li><a href="#updates">{t('navigation.updates')}</a></li>
            <li><a href="#contact">{t('navigation.contact')}</a></li>
          </ul>
        </nav>

        <div className="cookie-policy-main">
          <section id="what-are-cookies">
            <h2>{t('whatAreCookies.title')}</h2>
            <p>{t('whatAreCookies.description1')}</p>
            <p>{t('whatAreCookies.description2')}</p>
            
            <h3>{t('whatAreCookies.similarTechnologies.title')}</h3>
            <p>{t('whatAreCookies.similarTechnologies.description')}</p>
            <ul>
              <li><strong>{t('whatAreCookies.similarTechnologies.localStorage')}</strong></li>
              <li><strong>{t('whatAreCookies.similarTechnologies.sessionStorage')}</strong></li>
              <li><strong>{t('whatAreCookies.similarTechnologies.webBeacons')}</strong></li>
              <li><strong>{t('whatAreCookies.similarTechnologies.pixels')}</strong></li>
            </ul>
          </section>

          <section id="why-we-use-cookies">
            <h2>{t('whyWeUseCookies.title')}</h2>
            <p>{t('whyWeUseCookies.description')}</p>
            
            <h3>{t('whyWeUseCookies.essentialFunctionality.title')}</h3>
            <ul>
              <li><strong>{t('whyWeUseCookies.essentialFunctionality.authentication')}</strong></li>
              <li><strong>{t('whyWeUseCookies.essentialFunctionality.security')}</strong></li>
              <li><strong>{t('whyWeUseCookies.essentialFunctionality.navigation')}</strong></li>
              <li><strong>{t('whyWeUseCookies.essentialFunctionality.formData')}</strong></li>
            </ul>

            <h3>{t('whyWeUseCookies.userExperience.title')}</h3>
            <ul>
              <li><strong>{t('whyWeUseCookies.userExperience.languagePreferences')}</strong></li>
              <li><strong>{t('whyWeUseCookies.userExperience.themeSettings')}</strong></li>
              <li><strong>{t('whyWeUseCookies.userExperience.viewPreferences')}</strong></li>
              <li><strong>{t('whyWeUseCookies.userExperience.notifications')}</strong></li>
            </ul>

            <h3>{t('whyWeUseCookies.performanceAnalytics.title')}</h3>
            <ul>
              <li><strong>{t('whyWeUseCookies.performanceAnalytics.usageAnalytics')}</strong></li>
              <li><strong>{t('whyWeUseCookies.performanceAnalytics.performanceMonitoring')}</strong></li>
              <li><strong>{t('whyWeUseCookies.performanceAnalytics.featureUsage')}</strong></li>
              <li><strong>{t('whyWeUseCookies.performanceAnalytics.errorTracking')}</strong></li>
            </ul>
          </section>

          <section id="types-of-cookies">
            <h2>{t('typesOfCookies.title')}</h2>
            
            <h3>{t('typesOfCookies.byDuration.title')}</h3>
            <h4>{t('typesOfCookies.byDuration.sessionCookies.title')}</h4>
            <p>{t('typesOfCookies.byDuration.sessionCookies.description')}</p>
            <ul>
              <li>{t('typesOfCookies.byDuration.sessionCookies.maintainLogin')}</li>
              <li>{t('typesOfCookies.byDuration.sessionCookies.trackSearch')}</li>
              <li>{t('typesOfCookies.byDuration.sessionCookies.rememberForm')}</li>
              <li>{t('typesOfCookies.byDuration.sessionCookies.ensureSecurity')}</li>
            </ul>

            <h4>{t('typesOfCookies.byDuration.persistentCookies.title')}</h4>
            <p>{t('typesOfCookies.byDuration.persistentCookies.description')}</p>
            <ul>
              <li>{t('typesOfCookies.byDuration.persistentCookies.rememberCredentials')}</li>
              <li>{t('typesOfCookies.byDuration.persistentCookies.savePreferences')}</li>
              <li>{t('typesOfCookies.byDuration.persistentCookies.personalizedRecommendations')}</li>
              <li>{t('typesOfCookies.byDuration.persistentCookies.analyzeLongTerm')}</li>
            </ul>

            <h3>{t('typesOfCookies.byPurpose.title')}</h3>
            <h4>{t('typesOfCookies.byPurpose.strictlyNecessary.title')}</h4>
            <p>{t('typesOfCookies.byPurpose.strictlyNecessary.description')}</p>
            <ul>
              <li><strong>{t('typesOfCookies.byPurpose.strictlyNecessary.authenticationCookies')}</strong></li>
              <li><strong>{t('typesOfCookies.byPurpose.strictlyNecessary.securityCookies')}</strong></li>
              <li><strong>{t('typesOfCookies.byPurpose.strictlyNecessary.loadBalancingCookies')}</strong></li>
              <li><strong>{t('typesOfCookies.byPurpose.strictlyNecessary.sessionManagement')}</strong></li>
            </ul>

            <h4>{t('typesOfCookies.byPurpose.preferenceCookies.title')}</h4>
            <p>{t('typesOfCookies.byPurpose.preferenceCookies.description')}</p>
            <ul>
              <li><strong>{t('typesOfCookies.byPurpose.preferenceCookies.languageSettings')}</strong></li>
              <li><strong>{t('typesOfCookies.byPurpose.preferenceCookies.themePreferences')}</strong></li>
              <li><strong>{t('typesOfCookies.byPurpose.preferenceCookies.locationSettings')}</strong></li>
              <li><strong>{t('typesOfCookies.byPurpose.preferenceCookies.viewModes')}</strong></li>
            </ul>

            <h4>{t('typesOfCookies.byPurpose.analyticsCookies.title')}</h4>
            <p>{t('typesOfCookies.byPurpose.analyticsCookies.description')}</p>
            <ul>
              <li><strong>{t('typesOfCookies.byPurpose.analyticsCookies.usageStatistics')}</strong></li>
              <li><strong>{t('typesOfCookies.byPurpose.analyticsCookies.featureAnalytics')}</strong></li>
              <li><strong>{t('typesOfCookies.byPurpose.analyticsCookies.performanceMetrics')}</strong></li>
              <li><strong>{t('typesOfCookies.byPurpose.analyticsCookies.userJourneyTracking')}</strong></li>
            </ul>

            <h4>{t('typesOfCookies.byPurpose.marketingCookies.title')}</h4>
            <p>{t('typesOfCookies.byPurpose.marketingCookies.description')}</p>
            <ul>
              <li><strong>{t('typesOfCookies.byPurpose.marketingCookies.targetedAdvertising')}</strong></li>
              <li><strong>{t('typesOfCookies.byPurpose.marketingCookies.retargeting')}</strong></li>
              <li><strong>{t('typesOfCookies.byPurpose.marketingCookies.campaignTracking')}</strong></li>
              <li><strong>{t('typesOfCookies.byPurpose.marketingCookies.socialMediaIntegration')}</strong></li>
            </ul>
          </section>

          <section id="first-party-cookies">
            <h2>{t('firstPartyCookies.title')}</h2>
            <p>{t('firstPartyCookies.description')}</p>

            <div className="cookie-table-container">
              <table className="cookie-table">
                <thead>
                  <tr>
                    <th>{t('firstPartyCookies.table.cookieName')}</th>
                    <th>{t('firstPartyCookies.table.purpose')}</th>
                    <th>{t('firstPartyCookies.table.duration')}</th>
                    <th>{t('firstPartyCookies.table.type')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>{t('firstPartyCookies.table.rows.accessToken.name')}</code></td>
                    <td>{t('firstPartyCookies.table.rows.accessToken.purpose')}</td>
                    <td>{t('firstPartyCookies.table.rows.accessToken.duration')}</td>
                    <td>{t('firstPartyCookies.table.rows.accessToken.type')}</td>
                  </tr>
                  <tr>
                    <td><code>{t('firstPartyCookies.table.rows.refreshToken.name')}</code></td>
                    <td>{t('firstPartyCookies.table.rows.refreshToken.purpose')}</td>
                    <td>{t('firstPartyCookies.table.rows.refreshToken.duration')}</td>
                    <td>{t('firstPartyCookies.table.rows.refreshToken.type')}</td>
                  </tr>
                  <tr>
                    <td><code>{t('firstPartyCookies.table.rows.language.name')}</code></td>
                    <td>{t('firstPartyCookies.table.rows.language.purpose')}</td>
                    <td>{t('firstPartyCookies.table.rows.language.duration')}</td>
                    <td>{t('firstPartyCookies.table.rows.language.type')}</td>
                  </tr>
                  <tr>
                    <td><code>{t('firstPartyCookies.table.rows.theme.name')}</code></td>
                    <td>{t('firstPartyCookies.table.rows.theme.purpose')}</td>
                    <td>{t('firstPartyCookies.table.rows.theme.duration')}</td>
                    <td>{t('firstPartyCookies.table.rows.theme.type')}</td>
                  </tr>
                  <tr>
                    <td><code>{t('firstPartyCookies.table.rows.viewMode.name')}</code></td>
                    <td>{t('firstPartyCookies.table.rows.viewMode.purpose')}</td>
                    <td>{t('firstPartyCookies.table.rows.viewMode.duration')}</td>
                    <td>{t('firstPartyCookies.table.rows.viewMode.type')}</td>
                  </tr>
                  <tr>
                    <td><code>{t('firstPartyCookies.table.rows.locationCache.name')}</code></td>
                    <td>{t('firstPartyCookies.table.rows.locationCache.purpose')}</td>
                    <td>{t('firstPartyCookies.table.rows.locationCache.duration')}</td>
                    <td>{t('firstPartyCookies.table.rows.locationCache.type')}</td>
                  </tr>
                  <tr>
                    <td><code>{t('firstPartyCookies.table.rows.consent.name')}</code></td>
                    <td>{t('firstPartyCookies.table.rows.consent.purpose')}</td>
                    <td>{t('firstPartyCookies.table.rows.consent.duration')}</td>
                    <td>{t('firstPartyCookies.table.rows.consent.type')}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section id="third-party-cookies">
            <h2>{t('thirdPartyCookies.title')}</h2>
            <p>{t('thirdPartyCookies.description')}</p>

            <h3>{t('thirdPartyCookies.googleServices.title')}</h3>
            <ul>
              <li><strong>{t('thirdPartyCookies.googleServices.googleMaps')}</strong></li>
              <li><strong>{t('thirdPartyCookies.googleServices.googleFonts')}</strong></li>
              <li><strong>{t('thirdPartyCookies.googleServices.googleOAuth')}</strong></li>
            </ul>

            <h3>{t('thirdPartyCookies.paymentProcessing.title')}</h3>
            <ul>
              <li><strong>{t('thirdPartyCookies.paymentProcessing.stripe')}</strong></li>
            </ul>

            <h3>{t('thirdPartyCookies.socialMedia.title')}</h3>
            <ul>
              <li><strong>{t('thirdPartyCookies.socialMedia.facebook')}</strong></li>
            </ul>

            <h3>{t('thirdPartyCookies.analyticsPerformance.title')}</h3>
            <ul>
              <li><strong>{t('thirdPartyCookies.analyticsPerformance.cloudflare')}</strong></li>
              <li><strong>{t('thirdPartyCookies.analyticsPerformance.supabaseAnalytics')}</strong></li>
            </ul>

            <h3>{t('thirdPartyCookies.communication.title')}</h3>
            <ul>
              <li><strong>{t('thirdPartyCookies.communication.resend')}</strong></li>
            </ul>
          </section>

          <section id="conclusion">
            <h2>{t('summary.title')}</h2>
            <p>{t('summary.description1')}</p>
            <p>{t('summary.description2')}</p>
            <div className="key-points-box">
              <h4>{t('summary.keyTakeaways.title')}</h4>
              <ul>
                <li>{t('summary.keyTakeaways.enhance')}</li>
                <li>{t('summary.keyTakeaways.control')}</li>
                <li>{t('summary.keyTakeaways.transparency')}</li>
                <li>{t('summary.keyTakeaways.privacy')}</li>
                <li>{t('summary.keyTakeaways.contact')}</li>
              </ul>
            </div>
          </section>
        </div>
      </div>

      <div className="cookie-policy-footer">
        <button onClick={() => navigate(-1)} className="cookie-back-button">
          {t('buttons.goBack')}
        </button>
        <button onClick={() => navigate('/')} className="cookie-home-button">
          {t('buttons.homePage')}
        </button>
      </div>
    </div>
  );
};

export default CookiePolicy;